import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';

// Base protected route component
export const ProtectedRoute = ({ children, redirectTo = '/login' }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with return url
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return children;
};

// Private route for authenticated users only
export const PrivateRoute = ({ children }) => {
  return (
    <ProtectedRoute redirectTo="/login">
      {children}
    </ProtectedRoute>
  );
};

// Admin route for admin users only
export const AdminRoute = ({ children }) => {
  const { hasRole, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasRole('admin')) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location,
          message: 'Admin access required'
        }} 
        replace 
      />
    );
  }

  return children;
};

// Trainer route for trainer users only
export const TrainerRoute = ({ children }) => {
  const { hasRole, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasRole('trainer')) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location,
          message: 'Trainer access required'
        }} 
        replace 
      />
    );
  }

  return children;
};

// Role-based route for multiple roles
export const RoleBasedRoute = ({ children, allowedRoles = [] }) => {
  const { hasAnyRole, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !hasAnyRole(allowedRoles)) {
    return (
      <Navigate 
        to="/unauthorized" 
        state={{ 
          from: location,
          message: `Access restricted to: ${allowedRoles.join(', ')}`
        }} 
        replace 
      />
    );
  }

  return children;
};

// Public route that redirects authenticated users to dashboard
export const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Facility access route for users who can access facilities
export const FacilityAccessRoute = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user can access facilities
  // Admins, trainers, and receptionists can always access (for management purposes)
  // Members need active registration status
  const isStaff = user?.role === 'admin' || user?.role === 'trainer' || user?.role === 'receptionist';
  const canAccessFacilities = isStaff || (user?.registrationStatus === 'active' && user?.isActive);

  if (!canAccessFacilities) {
    return (
      <Navigate
        to="/dashboard"
        state={{
          from: location,
          message: 'Your account must be verified and payment completed to access gym facilities'
        }}
        replace
      />
    );
  }

  return children;
};

// All components are exported as named exports